import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Text } from '@rsos/components/capstone/base';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import { GPXFeature } from '@rsos/gainsight';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { OVERVIEW_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { RootState } from '../../../../app';
import { formatDateWithLocalTimezone } from '../../../../utils/dateUtilities';
import { CollapseButton } from '../SharedChatUI/CollapseButton';
import { TranslationMenu } from '../SharedChatUI/TranslationMenu';
import { TranslationMenuButton } from '../SharedChatUI/TranslationMenuButton';
import { ActionsBox, HeaderContainer, RedCircle } from './TF911.styles';
import TF911ChatHeaderButtons from './TF911ChatHeaderButtons';

interface TF911ChatHeaderProps {
  isForOverview?: boolean;
  onStartConversation: (message?: string) => Promise<{ isSuccess: boolean }>;
  showStartChat: boolean;
  gpxFeature?: GPXFeature;
}

const TF911ChatHeader = ({
  isForOverview = false,
  onStartConversation,
  showStartChat,
  gpxFeature,
}: TF911ChatHeaderProps) => {
  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const conversationChatHeader = useMemo(() => {
    if (collaborator.type) {
      if (isOutOfJurisdiction) {
        return 'Citizen Live Chat - Read Only';
      }
      return 'Citizen Live Chat';
    }
    return 'Live Chat';
  }, [collaborator.type, isOutOfJurisdiction]);

  const displayPhoneNumber =
    formatInternationalPhoneNumberHuman(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(
    selectedCallerID ?? '',
  );

  const displayStartTime = conversation?.data?.createdAt
    ? formatDateWithLocalTimezone(
        conversation.data.createdAt,
        'YYYY-MM-DD HH:mm',
      )
    : null;

  const isFetchingMetadata = useMemo(
    () => conversation?.loading?.getConversationMetadata,
    [conversation],
  );

  const isCreatingConversation = useMemo(
    () => conversation?.loading?.createConversation,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const onOverviewTab = useSelector(
    (state: RootState) => state.eventPane?.selectedTab === OVERVIEW_TAB,
  );

  return (
    <HeaderContainer
      data-name="text-from-911-chat-header"
      p={isForOverview ? '16px' : '12px'}
    >
      <Box horizontal justify="space-between" width="100%">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {!isFetchingMetadata &&
                conversation?.data?.conversation_id &&
                !isConversationEnded && <RedCircle />}
              {!isFetchingMetadata &&
                (!isConversationEnded ? conversationChatHeader : 'Closed Chat')}
            </Text>
            <Box horizontal>
              <Text color="secondaryText" fontWeight="regular" size="xsmall">
                {displayPhoneNumber}
              </Text>
              {displayStartTime && (
                <Text color="secondaryText" fontWeight="regular" size="xsmall">
                  &nbsp;&middot; Started {displayStartTime}
                </Text>
              )}
            </Box>
          </Text>
        </Box>
        {!onOverviewTab && (
          <ActionsBox>
            {!showStartChat && <TranslationMenuButton />}
            <CollapseButton />
          </ActionsBox>
        )}
      </Box>
      {!onOverviewTab && !showStartChat && <TranslationMenu />}
      {!isOutOfJurisdiction && selectedCallerID && (
        <TF911ChatHeaderButtons
          isFetchingMetadata={!!isFetchingMetadata}
          showStartChat={showStartChat}
          isCreatingConversation={!!isCreatingConversation}
          isForOverview={isForOverview}
          onStartConversation={onStartConversation}
          selectedCallerID={selectedCallerID}
          gpxFeature={gpxFeature}
        />
      )}
    </HeaderContainer>
  );
};

export default TF911ChatHeader;
